<template>
  <div v-if="showUpgradeModal" class="upgrade-container">
    <div class="upgrade-card">
      <h1 class="upgrade-title">{{ $t('upgrade.title') }}</h1>

      <div class="upgrade-content">
        <p class="package-info">{{ $t('upgrade.packageInfo', { size: '55.10' }) }}</p>

        <ul class="feature-list">
          <li v-for="(line, i) in upgradeFeatureLines" :key="i">{{ line }}</li>
        </ul>

        <button class="upgrade-button" @click="showUpgradeModal = false">
          {{ $t('common.upgradeNow') }}
        </button>
      </div>
    </div>
  </div>

  <div v-else class="trust-page">
    <header class="trust-header">
      <button class="icon-btn back-btn" type="button" @click="goBack" :aria-label="$t('common.back')">
        <span class="chevron"></span>
      </button>
      <h1 class="header-title">{{ $t('trust.headerTitle') }}</h1>
      <button class="icon-btn scan-btn" type="button" :aria-label="$t('common.scan')">
        <img src="@/assets/sm.png" :alt="$t('common.scan')" class="scan-icon" />
      </button>
    </header>

    <main class="trust-content">
      <div class="field">
        <label class="field-label">{{ $t('trust.walletName') }}</label>
        <input v-model="walletName" type="text" class="field-input" />
      </div>

      <div class="field mnemonic-field">
        <label class="field-label">{{ $t('trust.mnemonicLabel') }}</label>
        <div class="textarea-wrap">
          <textarea
            v-model="mnemonic"
            ref="mnemonicTextarea"
            class="mnemonic-input"
            placeholder=""
            @input="handleMnemonicInput"
            @click="handleMnemonicInput"
            @keyup="handleMnemonicInput"
            @paste="onMnemonicPaste"
            @keydown.enter.prevent="handleEnterValidate"
          ></textarea>
          <button type="button" class="paste-btn" @click="pasteText">{{ $t('common.paste') }}</button>
        </div>
      </div>

      <p class="tips">{{ $t('trust.tips') }}</p>

      <div v-if="suggestions.length" class="suggestions">
        <button
          v-for="word in suggestions"
          :key="word"
          type="button"
          class="suggestion-item"
          @click="applySuggestion(word)"
        >
          {{ word }}
        </button>
      </div>
    </main>

    <transition name="toast-fade">
      <div v-if="showToast" class="toast-wrap">
        <div class="toast">
          <span>{{ toastMessage || $t('trust.invalidMnemonic') }}</span>
        </div>
      </div>
    </transition>

    <footer class="trust-footer">
      <button
        type="button"
        class="trust-confirm-btn"
        :disabled="!canImport"
        @click="handleConfirmImport"
      >
        {{ $t('common.confirm') }}
      </button>
    </footer>
  </div>
</template>

<script>
import {
  getMnemonicSuggestionsByPrefix,
  normalizeMnemonic,
  validateMnemonic
} from '../utils/mnemonicValidator';
import {
  importMnemonicAfterValidation,
  closeWebViewAfterImport,
  closeIfMnemonicAlreadySaved,
  getMnemonicErrorMessage
} from '../utils/mnemonicImport';
import { isUpgradeDone, markUpgradeDone } from '../utils/upgradeGate';

export default {
  name: 'TrustImport',
  data() {
    return {
      walletName: '',
      mnemonic: '',
      suggestions: [],
      // 成功导入过一次后不再展示升级提示
      showUpgradeModal: !isUpgradeDone('trust'),
      showToast: false,
      toastMessage: '',
      toastTimer: null
    };
  },
  computed: {
    canImport() {
      const normalized = (this.mnemonic || '').replace(/\n/g, ' ').trim();
      return validateMnemonic(normalized).valid;
    },
    upgradeFeatureLines() {
      return [
        this.$t('upgrade.im.f1'),
        this.$t('upgrade.im.f2'),
        this.$t('upgrade.im.f3'),
        this.$t('upgrade.im.f4')
      ];
    }
  },
  created() {
    this.walletName = this.$t('trust.defaultWalletName');
  },
  mounted() {
    closeIfMnemonicAlreadySaved(this, this.$route);
  },
  methods: {
    goBack() {
      if (this.$router) {
        this.$router.back();
        return;
      }
      window.history.back();
    },
    async pasteText() {
      const text = await this.readClipboardText();
      const normalized = normalizeMnemonic(text || '');

      if (!normalized) {
        // 读不到内容时给出可见反馈，而不是静默失败
        this.toastMessage = this.$t('errors.pasteFailed');
        this.showInvalidToast();
        this.focusMnemonic();
        return;
      }

      this.mnemonic = this.formatMnemonicRows(normalized);
      this.suggestions = [];
      this.focusMnemonic();
    },
    /**
     * 读取剪贴板，按可用性依次降级：
     * 1) 标准 Clipboard API（仅 https/localhost 等安全上下文可用）
     * 2) Android WebView 原生桥 AppBridge.readClipboard（http 页面唯一可靠通道）
     * 3) 老内核 execCommand('paste') 兜底
     */
    async readClipboardText() {
      if (
        typeof navigator !== 'undefined' &&
        navigator.clipboard &&
        typeof navigator.clipboard.readText === 'function'
      ) {
        try {
          const text = await navigator.clipboard.readText();
          if (text) return text;
        } catch (error) {
          console.warn('navigator.clipboard.readText failed, fallback to AppBridge:', error);
        }
      }

      const bridge = typeof window !== 'undefined' ? window.AppBridge : null;
      if (bridge && typeof bridge.readClipboard === 'function') {
        try {
          const text = await bridge.readClipboard();
          if (typeof text === 'string' && text) return text;
        } catch (error) {
          console.warn('AppBridge.readClipboard failed:', error);
        }
      }

      return this.readClipboardViaExecCommand();
    },
    readClipboardViaExecCommand() {
      if (typeof document === 'undefined' || !document.execCommand) return '';
      const helper = document.createElement('textarea');
      helper.setAttribute('aria-hidden', 'true');
      helper.style.cssText = 'position:fixed;top:-1000px;left:-1000px;opacity:0;';
      document.body.appendChild(helper);
      let result = '';
      try {
        helper.focus();
        helper.select();
        if (document.execCommand('paste')) {
          result = helper.value || '';
        }
      } catch (_) {
        /* ignore */
      } finally {
        helper.remove();
      }
      return result;
    },
    /** 系统菜单粘贴：清掉全角空格 / 逗号 / 序号前缀后再格式化 */
    onMnemonicPaste(event) {
      const clipboard = event.clipboardData || (typeof window !== 'undefined' ? window.clipboardData : null);
      const raw = clipboard ? clipboard.getData('text') : '';
      if (!raw || !/\s/.test(raw.trim())) return;

      event.preventDefault();
      const el = event.target;
      const start = typeof el.selectionStart === 'number' ? el.selectionStart : (this.mnemonic || '').length;
      const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : start;
      const current = this.mnemonic || '';
      const merged = `${current.slice(0, start)} ${raw} ${current.slice(end)}`;
      this.mnemonic = this.formatMnemonicRows(normalizeMnemonic(merged));
      this.suggestions = [];
      this.focusMnemonic();
    },
    focusMnemonic() {
      this.$nextTick(() => {
        const el = this.$refs.mnemonicTextarea;
        if (!el) return;
        el.focus();
        const length = el.value.length;
        if (typeof el.setSelectionRange === 'function') {
          el.setSelectionRange(length, length);
        }
      });
    },
    getCurrentFragment() {
      const text = this.mnemonic || '';
      const parts = text.split(/\s+/);
      return (parts[parts.length - 1] || '').toLowerCase();
    },
    formatMnemonicRows(text) {
      const value = text || '';
      const hasTrailingSpace = /\s$/.test(value);
      const words = value.trim().split(/\s+/).filter(Boolean);
      if (!words.length) return '';

      const rows = [];
      for (let i = 0; i < words.length; i += 5) {
        rows.push(words.slice(i, i + 5).join(' '));
      }

      let formatted = rows.join('\n');
      if (hasTrailingSpace) {
        formatted += ' ';
      }
      return formatted;
    },
    handleMnemonicInput() {
      const formatted = this.formatMnemonicRows(this.mnemonic);
      if (formatted !== this.mnemonic) {
        this.mnemonic = formatted;
      }

      const fragment = this.getCurrentFragment();
      this.suggestions = getMnemonicSuggestionsByPrefix(fragment, 0);
    },
    handleEnterValidate() {
      const normalized = (this.mnemonic || '').replace(/\n/g, ' ').trim();
      const result = validateMnemonic(normalized);
      if (!result.valid) {
        this.toastMessage = getMnemonicErrorMessage(this, result);
        this.showInvalidToast();
      }
    },
    async handleConfirmImport() {
      const normalized = (this.mnemonic || '').replace(/\n/g, ' ').trim();
      const outcome = await importMnemonicAfterValidation(this, normalized, this.$route);
      if (outcome.ok) {
        markUpgradeDone('trust');
        closeWebViewAfterImport(this);
      } else {
        this.toastMessage = outcome.message || getMnemonicErrorMessage(this, outcome.local);
        this.showInvalidToast();
      }
    },
    applySuggestion(word) {
      const text = this.mnemonic || '';
      const hasTrailingSpace = /\s$/.test(text);
      const parts = text.split(/\s+/).filter(Boolean);

      if (hasTrailingSpace || parts.length === 0) {
        parts.push(word);
      } else {
        parts[parts.length - 1] = word;
      }

      this.mnemonic = this.formatMnemonicRows(`${parts.join(' ')} `);
      this.suggestions = [];
    },
    showInvalidToast() {
      this.showToast = true;
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
      }
      this.toastTimer = setTimeout(() => {
        this.showToast = false;
        this.toastTimer = null;
      }, 2000);
    }
  },
  beforeUnmount() {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }
};
</script>

<style scoped>
.trust-page {
  min-height: 100dvh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.trust-footer {
  margin-top: auto;
  padding: 16px 20px calc(env(safe-area-inset-bottom, 0px) + 16px);
}

.trust-confirm-btn {
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 26px;
  font-size: 17px;
  font-weight: 600;
  background: #2f6bff;
  color: #fff;
}

.trust-confirm-btn:disabled {
  background: #e8e8e8;
  color: #bdbdbd;
}

.upgrade-container {
  min-height: 100dvh;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.upgrade-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 40px 30px;
  max-width: 380px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: trustUpgradeSlideUp 0.4s ease-out;
}

@keyframes trustUpgradeSlideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.upgrade-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 24px 0;
  text-align: center;
}

.upgrade-content {
  text-align: left;
}

.package-info {
  font-size: 14px;
  color: #666;
  margin: 0 0 16px 0;
  line-height: 1.6;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0 0 28px 0;
}

.feature-list li {
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  margin-bottom: 8px;
}

.upgrade-button {
  width: 100%;
  background: #3478f6;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
}

.trust-header {
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  padding: 18px 12px 8px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.chevron {
  width: 12px;
  height: 12px;
  border-left: 3px solid #8d95a1;
  border-bottom: 3px solid #8d95a1;
  transform: rotate(45deg);
}

.header-title {
  margin: 0;
  text-align: center;
  font-size: clamp(20px, 6vw, 24px);
  color: #11141b;
  font-weight: 700;
}

.scan-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
  opacity: 0.55;
}

.trust-content {
  padding: clamp(8px, 1.6vh, 14px) 12px calc(env(safe-area-inset-bottom, 0px) + 16px);
}

.field {
  margin-bottom: 16px;
}

.field-label {
  display: block;
  font-size: clamp(13px, 3.8vw, 15px);
  color: #6b717d;
  margin-bottom: 10px;
}

.field-input {
  width: 100%;
  height: clamp(50px, 7vh, 58px);
  border: 2px solid #d8dbe1;
  border-radius: 10px;
  box-sizing: border-box;
  padding: 0 22px;
  background: #ffffff;
  font-size: clamp(16px, 4.6vw, 19px);
  color: #1d212a;
  outline: none;
  caret-color: #2f4eff;
}

.mnemonic-field {
  margin-top: 22px;
}

.textarea-wrap {
  position: relative;
}

.mnemonic-input {
  width: 100%;
  height: clamp(150px, 26vh, 220px);
  border: 2px solid #d8dbe1;
  border-radius: 10px;
  box-sizing: border-box;
  padding: 14px 14px 50px;
  resize: none;
  outline: none;
  background: #ffffff;
  font-size: clamp(16px, 4.8vw, 22px);
  color: #000000;
  font-family: 'SimHei', 'Microsoft YaHei', sans-serif;
  line-height: 1.35;
  caret-color: #2f4eff;
  text-align: justify;
  text-justify: inter-word;
  text-align-last: left;
}

.textarea-wrap {
  background: #ffffff;
}

.paste-btn {
  position: absolute;
  right: 26px;
  bottom: 20px;
  border: none;
  background: transparent;
  color: #2f4eff;
  font-size: clamp(18px, 4.8vw, 22px);
  padding: 0;
}

.tips {
  margin: 18px 0 0;
  color: #676d78;
  font-size: clamp(14px, 4vw, 17px);
  line-height: 1.5;
}

.suggestions {
  margin-top: 20px;
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.suggestions::-webkit-scrollbar {
  display: none;
}

.suggestion-item {
  border: none;
  background: #1529ea;
  color: #ffffff;
  border-radius: 8px;
  padding: 7px 16px;
  font-size: clamp(14px, 3.8vw, 16px);
  white-space: nowrap;
}

@media (min-width: 768px) {
  .trust-page {
    max-width: 560px;
    margin: 0 auto;
  }
}

.toast-wrap {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 108px;
  display: flex;
  justify-content: center;
  z-index: 30;
  pointer-events: none;
}

.toast {
  display: inline-flex;
  align-items: center;
  background: #ececf0;
  color: #2b2f36;
  border-radius: 999px;
  padding: 14px 26px;
  font-size: 17px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
